import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SectionTitle, SectionSubtitle } from '../../components/ui/Layout';
import { Table, TableWrap } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { Select } from '../../components/ui/FormControls';
import { Spinner, ErrorMessage } from '../../components/ui/Feedback';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';

const Toolbar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const LiveDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.green};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 6px currentColor;
  }
`;

const StatusSelect = styled(Select)`
  min-height: 36px;
  padding: 0.35em 0.7em;
  font-size: 0.85rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  min-height: 36px;
  padding: 0.4em 0.9em;
  font-size: 0.82rem;
`;

const estados = ['pendiente', 'confirmada', 'cancelada'];

function AdminReservations() {
  const { token } = useAuth();
  const socket = useSocket();
  const [reservations, setReservations] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get('/api/admin/reservations', { token })
      .then(setReservations)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.emit('admin:subscribe');

    const handleNew = (reservation) => {
      setReservations((prev) => [reservation, ...prev]);
    };

    const handleUpdate = (reservation) => {
      setReservations((prev) =>
        prev.map((r) => (r.id === reservation.id ? reservation : r))
      );
    };

    socket.on('reservation:new', handleNew);
    socket.on('reservation:update', handleUpdate);

    return () => {
      socket.off('reservation:new', handleNew);
      socket.off('reservation:update', handleUpdate);
    };
  }, [socket]);

  const handleStatusChange = async (id, estado) => {
    const previous = reservations;
    setUpdatingId(id);
    setError('');
    setReservations((prev) => prev.map((r) => (r.id === id ? { ...r, estado } : r)));
    try {
      await api.patch(`/api/admin/reservations/${id}`, { estado }, { token });
    } catch (err) {
      setReservations(previous);
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = useMemo(
    () => (filtro ? reservations.filter((r) => r.estado === filtro) : reservations),
    [reservations, filtro]
  );

  return (
    <div>
      <SectionTitle $align="left">Reservaciones</SectionTitle>
      <SectionSubtitle $align="left">
        Se actualizan en tiempo real conforme llegan nuevas reservaciones.
      </SectionSubtitle>

      <Toolbar>
        <LiveDot>En vivo</LiveDot>
        <StatusSelect value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="">Todos los estados</option>
          {estados.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </StatusSelect>
      </Toolbar>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      {loading ? (
        <Spinner $size="28px" />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Personas</th>
                <th>Comentarios</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.nombre}</td>
                  <td>{r.telefono}</td>
                  <td>{r.fecha}</td>
                  <td>{String(r.hora).slice(0, 5)}</td>
                  <td>{r.personas}</td>
                  <td>{r.comentarios || '—'}</td>
                  <td>
                    <Badge $status={r.estado}>{r.estado}</Badge>
                  </td>
                  <td>
                    <RowActions>
                      {r.estado !== 'confirmada' && (
                        <ActionButton
                          type="button"
                          $variant="primary"
                          disabled={updatingId === r.id}
                          onClick={() => handleStatusChange(r.id, 'confirmada')}
                          aria-label={`Aceptar reservación de ${r.nombre}`}
                        >
                          {updatingId === r.id ? <Spinner $size="14px" /> : 'Aceptar'}
                        </ActionButton>
                      )}
                      {r.estado !== 'cancelada' && (
                        <ActionButton
                          type="button"
                          $variant="danger"
                          disabled={updatingId === r.id}
                          onClick={() => handleStatusChange(r.id, 'cancelada')}
                          aria-label={`Cancelar reservación de ${r.nombre}`}
                        >
                          Cancelar
                        </ActionButton>
                      )}
                      {r.estado !== 'pendiente' && (
                        <ActionButton
                          type="button"
                          $variant="ghost"
                          disabled={updatingId === r.id}
                          onClick={() => handleStatusChange(r.id, 'pendiente')}
                          aria-label={`Volver a pendiente la reservación de ${r.nombre}`}
                        >
                          Volver a pendiente
                        </ActionButton>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8}>No hay reservaciones para mostrar.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}

export default AdminReservations;
